import React, { FC, useState, useEffect } from "react";
import {
  Form,
  InputNumber,
  Radio,
  Typography,
  Input,
  Select,
  Spin,
} from "antd";
import { BarChartOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { toggleBreakdownModal } from "../../../../redux/actions/investmentForm";
import useInvestmentDurationTerms from "../../../../custom-hooks/useInvestmentDurationTerms";
import { formatMoney } from "../../../../helpers/formatter";
import styles from "../Components.module.scss";
import "../select-overrides.css";
import useInvestmentInterestTypes from "../../../../custom-hooks/useInvestmentInterestType";

const { Title, Text } = Typography;

// Simplified props interface
interface InvestmentModalDurationTermsProps {
  form: any;
  tenantId?: number;
  investRateId?: number;
  data?: any;
}

const InvestmentModalDurationTerms: FC<InvestmentModalDurationTermsProps> = ({
  form,
  tenantId,
  investRateId,
  data,
}) => {
  const dispatch = useDispatch();

  // Use our custom hook
  const {
    calculatedInterest,
    currencyCode,
    handleTenureChange,
    handleDaysInYearChange,
    handleInterestRepaymentTypeChange,
    hasCalculatedInterest,
    isCalculating,
  } = useInvestmentDurationTerms(form, tenantId, investRateId, data);

  const { data: investmentInterestTypes, isLoading: isLoadingInterestTypes } =
    useInvestmentInterestTypes();

  return (
    <div className={`investment-modal-duration-terms ${styles.formContainer}`}>
      <div className={styles.modalTitle}>Investment Duration</div>

      <div className={styles.formWrapper}>
        <Title level={5} style={{ marginBottom: "24px", fontWeight: 500 }}>
          How Long Do You Want To Invest?
        </Title>

        <Form.Item
          name="tenure"
          label={<span className={styles.formLabel}>Tenor(Days)</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "Please enter tenor in days" }]}
        >
          <InputNumber
            className={styles.inputStyle}
            style={{ width: "100%" }}
            min={1}
            placeholder="Enter number of days"
            onChange={(value) => {
              handleTenureChange(value);
            }}
            size="large"
            precision={0}
          />
        </Form.Item>

        <Form.Item
          name="interest_rate"
          label={<span className={styles.formLabel}>Interest Rate (%)</span>}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Input
            disabled
            className={`${styles.inputStyle} ${styles.disabledInput}`}
            style={{ width: "100%" }}
            suffix={isCalculating ? <LoadingOutlined /> : null}
          />
        </Form.Item>

        <Form.Item name="number_of_days_in_year" label="" initialValue={365}>
          <Select
            className={styles.selectStyle}
            style={{ width: "100%" }}
            size="large"
            onChange={handleDaysInYearChange}
          >
            <Select.Option value={360} style={{ fontSize: "16px" }}>
              360 Days
            </Select.Option>
            <Select.Option value={365} style={{ fontSize: "16px" }}>
              365 Days
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="interest_repayment_type"
          label={
            <span className={styles.formLabel}>Interest Repayment Type</span>
          }
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Select
            disabled
            className={styles.selectStyle}
            style={{ width: "100%" }}
            size="large"
            onChange={handleDaysInYearChange}
          >
            {investmentInterestTypes &&
              investmentInterestTypes.data.map((_interestType) => (
                <Select.Option
                  value={_interestType.id}
                  style={{ fontSize: "16px" }}
                >
                  {_interestType?.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        {/* <div style={{ marginTop: "24px", marginBottom: "16px" }}>
          <Text className={styles.formLabel}>
            Select an interest payment plan:
          </Text>
        </div>

        <Form.Item
          name="interest_repayment_type"
          rules={[
            { required: true, message: "Please select interest payment plan" },
          ]}
        >
          <Radio.Group
            onChange={handleInterestRepaymentTypeChange}
            size="large"
          >
            <Radio
              value="Monthly"
              style={{ fontSize: "16px", marginBottom: "16px" }}
            >
              Monthly
            </Radio>
            <div style={{ height: "8px" }}></div>
            <Radio value="EndOfTenure" style={{ fontSize: "16px" }}>
              End of Tenure
            </Radio>
          </Radio.Group>
        </Form.Item> */}

        {(hasCalculatedInterest || isCalculating) && (
          <div style={{ marginTop: "32px" }}>
            <Spin
              spinning={isCalculating}
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            >
              <div style={{ minHeight: "120px" }}>
                {hasCalculatedInterest && (
                  <>
                    <div style={{ marginBottom: "16px" }}>
                      <Text className={styles.formLabel} strong>
                        Estimated Returns:{" "}
                        {formatMoney(
                          calculatedInterest.maturity_amount || 0,
                          currencyCode,
                        )}
                      </Text>
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      <Text className={styles.formLabel} strong>
                        Total Interest Payable:{" "}
                        {formatMoney(
                          calculatedInterest.interest_calculated || 0,
                          currencyCode,
                        )}
                      </Text>
                    </div>
                    <div style={{ marginBottom: "24px" }}>
                      <Text className={styles.formLabel} strong>
                        Monthly Interest Payable:{" "}
                        {formatMoney(
                          calculatedInterest.monthly_interest_payable || 0,
                          currencyCode,
                        )}
                      </Text>
                    </div>
                    <div className={styles.breakdownLink}>
                      <Typography.Link
                        onClick={() => dispatch(toggleBreakdownModal(true))}
                        className={styles.link}
                      >
                        <BarChartOutlined style={{ fontSize: "16px" }} />
                        View Interest Breakdown
                      </Typography.Link>
                    </div>
                  </>
                )}
              </div>
            </Spin>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestmentModalDurationTerms;
